/**
 * Magnetic Story Card — Interactive Image+Text Section
 *
 * Glassmorphism card magnetically follows the cursor with
 * 3D tilt. Background image zooms on scroll (Ken Burns).
 * Content elements stagger in on scroll entry.
 *
 * Requires GSAP + ScrollTrigger (loaded by the section).
 */
(function () {
  'use strict';

  function waitForDeps(cb) {
    if (window.gsap && window.ScrollTrigger) return cb();
    var check = setInterval(function () {
      if (window.gsap && window.ScrollTrigger) {
        clearInterval(check);
        cb();
      }
    }, 50);
    setTimeout(function () { clearInterval(check); }, 8000);
  }

  function initMagneticCard(sectionId) {
    var section = document.querySelector('[data-magnetic-card-id="' + sectionId + '"]');
    if (!section) return;

    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    /* ── Read config from data attributes ── */
    var magneticEnabled = section.dataset.magneticEnabled === 'true' && !isTouch;
    var magneticStrength = parseFloat(section.dataset.magneticStrength) || 15;
    var tiltIntensity = parseFloat(section.dataset.tiltIntensity) || 6;
    var kenBurnsEnabled = section.dataset.kenBurnsEnabled === 'true';
    var kenBurnsScale = (parseFloat(section.dataset.kenBurnsScale) || 112) / 100;
    var entryAnim = section.dataset.entryAnimation || 'fade_up';
    var staggerDelay = (parseFloat(section.dataset.staggerDelay) || 100) / 1000;

    gsap.registerPlugin(ScrollTrigger);

    var bgImage = section.querySelector('.hp-magnetic-card__bg-image');
    var card = section.querySelector('.hp-magnetic-card__card');
    var cardChildren = card ? card.querySelectorAll('.hp-magnetic-card__block') : [];
    var triggers = [];
    var listeners = [];

    if (!card) return;

    /* ══════════════════════════════════════════
     *  KEN BURNS — scroll-linked background zoom
     * ══════════════════════════════════════════ */

    function initKenBurns() {
      if (!kenBurnsEnabled || !bgImage || reducedMotion) return;

      var tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 0.8
        }
      });

      tl.fromTo(bgImage,
        { scale: 1 },
        { scale: kenBurnsScale, ease: 'none', duration: 1 },
        0
      );

      triggers.push(tl.scrollTrigger);
    }

    /* ══════════════════════════════════════════
     *  STAGGERED CONTENT REVEAL
     * ══════════════════════════════════════════ */

    function initContentReveal() {
      if (cardChildren.length === 0) return;

      if (reducedMotion) {
        /* Show everything immediately */
        cardChildren.forEach(function (el) {
          gsap.set(el, { opacity: 1, y: 0, scale: 1 });
        });
        return;
      }

      /* Set initial hidden states */
      var fromProps;
      switch (entryAnim) {
        case 'fade_up':
          fromProps = { opacity: 0, y: 30 };
          break;
        case 'fade_in':
          fromProps = { opacity: 0 };
          break;
        case 'scale_up':
          fromProps = { opacity: 0, scale: 0.92 };
          break;
        default:
          fromProps = { opacity: 0, y: 30 };
      }

      cardChildren.forEach(function (el) {
        gsap.set(el, fromProps);
      });

      /* Animate in on scroll */
      var toProps;
      switch (entryAnim) {
        case 'fade_up':
          toProps = { opacity: 1, y: 0, ease: 'power2.out' };
          break;
        case 'fade_in':
          toProps = { opacity: 1, ease: 'power2.out' };
          break;
        case 'scale_up':
          toProps = { opacity: 1, scale: 1, ease: 'back.out(1.4)' };
          break;
        default:
          toProps = { opacity: 1, y: 0, ease: 'power2.out' };
      }

      toProps.duration = 0.7;
      toProps.stagger = staggerDelay;

      var st = ScrollTrigger.create({
        trigger: section,
        start: 'top 65%',
        once: true,
        onEnter: function () {
          gsap.to(cardChildren, toProps);
        }
      });

      triggers.push(st);
    }

    /* ══════════════════════════════════════════
     *  MAGNETIC CURSOR-FOLLOW + 3D TILT
     * ══════════════════════════════════════════ */

    function initMagnetic() {
      if (!magneticEnabled || reducedMotion) return;

      function onMouseMove(e) {
        var rect = section.getBoundingClientRect();
        var centerX = rect.left + rect.width / 2;
        var centerY = rect.top + rect.height / 2;
        var halfW = rect.width / 2;
        var halfH = rect.height / 2;

        /* Normalised position: -1 to 1 */
        var dx = (e.clientX - centerX) / halfW;
        var dy = (e.clientY - centerY) / halfH;

        /* Clamp to prevent extreme values */
        dx = Math.max(-1, Math.min(1, dx));
        dy = Math.max(-1, Math.min(1, dy));

        gsap.to(card, {
          x: dx * magneticStrength,
          y: dy * magneticStrength,
          rotateX: -dy * tiltIntensity,
          rotateY: dx * tiltIntensity,
          duration: 0.6,
          ease: 'power2.out',
          overwrite: 'auto'
        });
      }

      function onMouseLeave() {
        gsap.to(card, {
          x: 0,
          y: 0,
          rotateX: 0,
          rotateY: 0,
          duration: 0.8,
          ease: 'elastic.out(1, 0.5)',
          overwrite: 'auto'
        });
      }

      section.addEventListener('mousemove', onMouseMove);
      section.addEventListener('mouseleave', onMouseLeave);

      listeners.push(
        { el: section, type: 'mousemove', fn: onMouseMove },
        { el: section, type: 'mouseleave', fn: onMouseLeave }
      );
    }

    /* ── Boot all effects ── */
    initKenBurns();
    initContentReveal();
    initMagnetic();

    /* ── Cleanup for Shopify editor ── */
    section._magneticCardCleanup = function () {
      triggers.forEach(function (st) { st.kill(); });
      triggers = [];
      listeners.forEach(function (l) {
        l.el.removeEventListener(l.type, l.fn);
      });
      listeners = [];
    };
  }

  /* Boot */
  function boot() {
    document.querySelectorAll('[data-magnetic-card-id]').forEach(function (el) {
      var id = el.dataset.magneticCardId;
      if (!el._magneticCardInit) {
        el._magneticCardInit = true;
        waitForDeps(function () { initMagneticCard(id); });
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Shopify editor events */
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target.querySelector('[data-magnetic-card-id]');
    if (el) {
      el._magneticCardInit = false;
      waitForDeps(function () {
        initMagneticCard(el.dataset.magneticCardId);
        ScrollTrigger.refresh();
      });
    }
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var el = e.target.querySelector('[data-magnetic-card-id]');
    if (el && el._magneticCardCleanup) el._magneticCardCleanup();
  });
})();
